import { useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    InputAdornment,
    List,
    ListItem,
    ListItemText,
    Paper,
    TextField,
    Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import api from "../../api";
import { getSession, saveToken, saveSession } from "../../auth";
import useNotification from "../../components/Notification";

const SupportEnterprise = () => {
    const [search, setSearch] = useState("");
    const [enterprises, setEnterprises] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingAccess, setLoadingAccess] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadEnterprises = async () => {
            try {
                const response = await api.get("api/enterprises/simplified/");

                if (response.status === 200) {
                    setEnterprises(response.data);
                    return;
                }

                throw response.data;
            } catch (err) {
                console.error("Failed to load enterprises:", err);

                if (err.response?.status === 403) {
                    setError("Você não possui permissão para acessar o portal de suporte.");
                } else {
                    setError("Não foi possível carregar a lista de empresas. Tente novamente mais tarde.");
                }
            } finally {
                setLoading(false);
            }
        };

        loadEnterprises();
    }, []);

    const filtered = useMemo(() => {
        const value = search.toLowerCase();

        return enterprises.filter(
            (enterprise) =>
                enterprise.name.toLowerCase().includes(value) ||
                String(enterprise.accountcode).includes(value)
        );
    }, [search, enterprises]);

    const handleSelect = async (enterprise) => {
	    try {
	    	setLoadingAccess(true);
	        
            const response = await api.post(
	            "api/enterprises/support_login/",
	            {
	                enterprise: enterprise.id,
	            }
	        );

            if (response.status === 400) {
                if (response.data.enterprise_id?.[0] === "This enterprise has no administrator user.") {
                    useNotification(
                        "Atenção!",
                        "Esta empresa não possui nenhum usuário administrador disponível para viabilizar o acesso.",
                        "warning"
                    )
                    return
                }
                useNotification(
                    "Ops!",
                    "Algo deu errado, entre em contato com o time de desenvolvimento.",
                    "error"
                )
                return
            }

            if (response.data.scout) {
                window.location.href = `${response.data.scout_href}/sso?token=${response.data.access}`
                return
            }

	        const agentId = response?.data?.user?.profile?.agent?.id

            if (! agentId) return
            
            // Only save token if user has an agent related
            saveToken(response.data.access)

            // Login related agent
            await api.post(`/api/IV/agents/${agentId}/login/`).then((res) => {
                if (res.status == 200) {
                    // Replacing agent data with new status
                    response.data.user.profile.agent = res.data
                    saveSession(response.data.user)
                    window.location.href = "/chat"
                    return
                }
                throw res
            }).catch((err) => {
                console.error("Couldnt login agent [err:%o]", err)
                clearToken()
                clearSession()
                setShowAlert("error", "Algo deu errado, tente novamente.")
                setLoadingAccess(false)
            })
	    } catch (err) {
	        console.error("Failed to switch enterprise:", err);
	    } finally {
	    	setLoadingAccess(false);
	    }
	};

    if (loading) {
        return (
            <Box
                sx={{
                    maxWidth: 700,
                    mx: "auto",
                    mt: 6,
                    p: 3,
                    textAlign: "center",
                }}
            >
                <Typography variant="h5" gutterBottom>
                    Acesso suporte
                </Typography>

                <CircularProgress sx={{ my: 3 }} />

                <Typography>
                    Carregando empresas...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box
                sx={{
                    maxWidth: 700,
                    mx: "auto",
                    mt: 6,
                    p: 3,
                }}
            >
                <Typography variant="h5" gutterBottom>
                    Acesso suporte
                </Typography>

                <Typography color="error">
                    {error}
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                maxWidth: 700,
                mx: "auto",
                mt: 6,
                p: 3,
            }}
        >
            <Typography variant="h4" gutterBottom>
                Acesso suporte
            </Typography>

            <Typography color="text.secondary" mb={3}>
                Selecione abaixo a empresa que deseja acessar.
            </Typography>

            <TextField
            	size="small"
                fullWidth
                variant="outlined"
                placeholder="Pesquisar por nome da empresa ou accountcode..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon />
                        </InputAdornment>
                    ),
                }}
            />

            <Paper
                variant="outlined"
                sx={{
                    mt: 3,
                    maxHeight: 450,
                    overflow: "auto",
                }}
            >
                {filtered.length === 0 ? (
                    <Typography
                        align="center"
                        color="text.secondary"
                        sx={{ py: 4 }}
                    >
                        Nenhuma empresa encontrada.
                    </Typography>
                ) : (
                    <List disablePadding>
                        {filtered.map((enterprise) => (
                            <ListItem
                                key={enterprise.id}
                                divider
                                secondaryAction={
                                    <Button
                                        variant="contained"
                            			disabled={loadingAccess}
                                        onClick={() =>
                                            handleSelect(enterprise)
                                        }
                                    >
                                        {!loadingAccess ? "Acessar": "Processando..."}
                                    </Button>
                                }
                            >
                                <ListItemText
                                    primary={enterprise.name}
                                    secondary={`Accountcode: ${enterprise.accountcode}`}
                                />
                            </ListItem>
                        ))}
                    </List>
                )}
            </Paper>
        </Box>
    );
};

export default SupportEnterprise;